import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {add, deleteRecord, fetch, update} from '../services/CardsService';
import {Card} from '../types';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';

export const useCard = (customerId: string, cardId?: string) => {

    if (typeof customerId === 'undefined') {
        throw new Error('customerId is required parameter');
    }

    const queryClient = useQueryClient();

    const cardQuery = useQuery({
        queryKey: ['card', customerId, cardId],
        queryFn: () => fetch(customerId, cardId!),
        enabled: !!cardId,
    });

    const createMutation = useMutation({
        mutationFn: (values: Card) => {
            return add({...values, customerId});
        },
        onSuccess: () => {
            notifySuccess('Card created');
            queryClient.invalidateQueries({queryKey: ['cards', customerId]});
        },
        onError: notifyError,
    });

    const updateMutation = useMutation({
        mutationFn: (values: Card) => {
            if (!cardId) throw new Error('cardId is required parameter');
            return update(values);
        },
        onSuccess: () => {
            notifySuccess('Card updated');
            queryClient.invalidateQueries({queryKey: ['cards', customerId]});
            queryClient.invalidateQueries({queryKey: ['card', customerId, cardId]});
        },
        onError: notifyError,
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!cardId) throw new Error('cardId is required parameter');
            return deleteRecord(customerId, cardId);
        },
        onSuccess: () => {
            notifySuccess('Card deleted');
            queryClient.invalidateQueries({queryKey: ['cards', customerId]});
        },
        onError: notifyError,
    });

    return {
        record: cardQuery.data,
        loading: cardQuery.isLoading,
        fetchRecord: cardQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation,
    };
};
